import React, { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "gatsby";
import { useIntl } from "gatsby-plugin-intl";
import sitemap from "@models/sitemap.json";
import welcomeBG01 from "@images/welcome/bg-01.jpg";
import welcomeBG02 from "@images/welcome/bg-02.png";
import welcomeBG03 from "@images/welcome/bg-03.png";
import welcomeBG04 from "@images/welcome/bg-04.png";
import gradientRight from "@images/welcome/gradient-right.png";
import gradientLeft from "@images/welcome/gradient-left.png";
import gradientTop from "@images/welcome/gradient-top.png";
import gradientBottom from "@images/welcome/gradient-bottom.png";
import speck from "@images/welcome/speck.png";
import dust1 from "@images/welcome/dust1.png";
import dust2 from "@images/welcome/dust2.png";
import pianoStatic from "@images/welcome/piano-static.png";
import pianoAnimated from "@images/welcome/piano-animated.gif";
import tipContainer from "@images/welcome/tip-container.png";
import tipPianoGlow from "@images/welcome/tip-piano-glow.png";
import playHintText from "@images/welcome/play-hint-text.png";
import headphoneIcon from "@images/welcome/headphone-icon.png";
import "@assets/css/welcome.scss";
import Parallax from "parallax-js";
import useMusicPlayer from "@hooks/useMusicPlayer";
import ReactPlayer from "react-player";
import welcomePianoMusic from "../assets/musics/welcome-piano.mp3";

const TWINKLE_SPEED = 400;
const PIANO_MUSIC_LENGTH = 4000;

const defaultWelcomeBGStyle = {
  width: "100vw",
  height: "100vh",
  objectFit: "cover",
};

const defaultSpeckStyle = {
  animationTimingFunction: "cubic-bezier(0.250, 0.250, 0.750, 0.750)",
  animationName: "twinkleMove",
  position: "absolute",
};

const defaultFloatImgWrapperStyle = {
  height: "120vh",
  overflow: "hidden",
};

const defaultFloatImgStyle = idx => ({
  opacity: idx === 1 ? 0.25 : 0.2,
  animationDuration: idx === 1 ? "450s" : "600s",
  position: "relative",
  height: "100000px",
  width: "100000px",
  top: "-8500px",
  left: "-8000px",
  backgroundRepeat: "repeat",
  animationTimingFunction: "cubic-bezier(0.250, 0.250, 0.750, 0.750)",
  animationName: "float",
  animationIterationCount: "infinite",
});

const fadeInStyle = {
  animation: "fadeIn linear 1s",
};

const absHorizontalCenter = {
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
};

const overlayStyle = {
  ...fadeInStyle,
  position: "fixed",
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(31,32,37,0.9)",
  zIndex: 800,
};

const overlayWhiteStyle = {
  ...overlayStyle,
  backgroundColor: "#fff",
  zIndex: 999999,
};

const pianoStyle = {
  ...absHorizontalCenter,
  ...fadeInStyle,
  width: "380px",
  height: "auto",
  top: "46%",
  zIndex: 1001,
  cursor: "pointer",
};

const tipPianoGlowStyle = {
  ...absHorizontalCenter,
  ...fadeInStyle,
  width: "125px",
  height: "auto",
  top: "46%",
  zIndex: 1002,
  cursor: "pointer",
};

const tipStyle = {
  container: {
    ...absHorizontalCenter,
    top: "32%",
    width: "370px",
    zIndex: 1001,
    animation: "fadeIn linear 1s, moveDown linear 1s forwards",
    backgroundImage: `url(${tipContainer})`,
    backgroundSize: "100% 100%",
    backgroundRepeat: "no-repeat",
    padding: "20px 20px 35px 20px",
  },
  headphoneHint: {
    color: "#918678",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "10px",
    lineHeight: "22px",
  },
  soundOpenRadios: {
    width: "100%",
    padding: "10px",
    borderRadius: "10px",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-evenly",
  },
};

const playHintTextStyle = {
  ...pianoStyle,
  width: "250px",
  top: "calc(46% + 213px)",
  animationName: "twinkle",
  animationDuration: "2s",
  animationIterationCount: "infinite",
  textAlign: "center",
};

const getBGStyle = tick =>
  tick >= 3
    ? { ...defaultWelcomeBGStyle, ...{ transition: "transform 3s", transform: "scale(1.1)" } }
    : defaultWelcomeBGStyle;

const gradients = [
  {
    src: gradientTop,
    style: {
      position: "absolute",
      top: "-50px",
      width: "100%",
      height: "auto",
      zIndex: 998,
    },
  },
  {
    src: gradientBottom,
    style: {
      position: "absolute",
      bottom: "-50px",
      width: "100%",
      height: "auto",
      zIndex: 998,
    },
  },
  {
    src: gradientRight,
    style: {
      position: "absolute",
      top: 0,
      right: 0,
      height: "100%",
      width: "auto",
      zIndex: 998,
    },
  },
  {
    src: gradientLeft,
    style: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "auto",
      zIndex: 998,
    },
  },
];

const WelcomePage = () => {
  const { locale, formatMessage } = useIntl();
  const sceneRef = useRef(null);
  const durationRef = useRef(0);
  const bgImgRef = useRef(null);

  const timeoutList = useRef([]);
  const [specks, setSpecks] = useState(new Array(6).fill(1));
  const [tick, setTick] = useState(0);

  const [pianoClicked, setPianoClicked] = useState(false);
  const [fadeOutStart, setFadeOutStart] = useState(false);
  const { musicOn, setMusicOn } = useMusicPlayer();

  useEffect(() => {
    new Parallax(sceneRef.current);
  }, []);

  useEffect(() => {
    const timeoutListRef = timeoutList;

    specks.forEach((_, idx) => {
      twinkleLoop(idx);
    });

    function twinkleLoop(idx) {
      durationRef.current = Math.random() * 5 + 3 - (495 - TWINKLE_SPEED) / 100;
      twinkle(idx, durationRef.current);
      const timeout = setTimeout(function () {
        twinkleLoop(idx);
      }, durationRef.current * 1000);
      timeoutListRef.current.push(timeout);
    }

    function twinkle(idx, duration) {
      const top = Math.floor(Math.random() * 85);
      const left = Math.floor(Math.random() * 85);
      const newSpec = {
        top: top + "%",
        left: left + "%",
        animationDuration: `${duration}s`,
      };
      setSpecks(prev => [...prev.slice(0, idx), newSpec, ...prev.slice(idx + 1)]);
    }

    return () => {
      timeoutListRef.current.forEach(el => {
        clearTimeout(el);
      });
    };
  }, []);

  useEffect(() => {
    if (!pianoClicked) return;

    // fadeout: fadeout Start
    const timeOut = setTimeout(() => {
      setFadeOutStart(true);
    }, PIANO_MUSIC_LENGTH);

    return () => {
      clearTimeout(timeOut);
    };
  }, [pianoClicked]);

  useEffect(() => {
    // fadeout: navigate to home page
    if (!fadeOutStart) return;

    const timeOut = setTimeout(() => {
      navigate(`/${locale}${sitemap.home.path}`, { replace: true });
    }, 1000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [fadeOutStart, locale]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handlePianoClicked = () => {
    setPianoClicked(true);
  };

  const SoundComponent = useMemo(
    () => <ReactPlayer url={welcomePianoMusic} playing={pianoClicked} volume={musicOn ? 1 : 0} />,
    [pianoClicked, musicOn]
  );

  return (
    <>
      {fadeOutStart && <div className="overlayWhite" style={overlayWhiteStyle} />}
      {tick >= 6 && <div className="overlay" style={overlayStyle} />}
      {tick >= 9 && (
        <div style={tipStyle.container}>
          <div style={tipStyle.headphoneHint}>
            <img
              src={headphoneIcon}
              style={{ marginRight: "8px", width: "30px" }}
              alt="headphone"
            />
            {formatMessage({ id: "welcome-headphone-hint" })}
          </div>
          <div style={tipStyle.soundOpenRadios}>
            <label className="radio-container">
              <input
                type="radio"
                name="sound"
                checked={musicOn}
                onChange={() => setMusicOn(true)}
              />
              <span className="label">{formatMessage({ id: "welcome-open-sound" })}</span>
            </label>
            <label className="radio-container">
              <input
                type="radio"
                name="sound"
                checked={!musicOn}
                onChange={() => setMusicOn(false)}
              />
              <span className="label">{formatMessage({ id: "welcome-close-sound" })}</span>
            </label>
          </div>
        </div>
      )}
      {tick >= 11 && (
        <img
          src={tipPianoGlow}
          alt="piano-tip"
          style={tipPianoGlowStyle}
          onClick={handlePianoClicked}
        />
      )}
      {tick >= 12 && (
        <div style={playHintTextStyle}>
          <div
            style={{
              textAlign: "center",
              color: "#fff",
              textShadow: "0 0 10px #ffffff, 2px 2px 7px rgba(255, 255, 255, 0.8)",
              fontSize: "30px",
              display: "flex",
              justifyContent: "space-between",
              padding: "0 15px 0 15px",
              marginBottom: "5px",
            }}>
            {formatMessage({ id: "welcome-piano-hint" })
              .split("")
              .map((char, index) => (
                <span key={index}>{char}</span>
              ))}
          </div>
          <img src={playHintText} alt="hint-text" style={{ width: "100%" }} />
        </div>
      )}
      {tick >= 8 && (
        <>
          <img
            src={pianoClicked ? pianoAnimated : pianoStatic}
            alt="piano-clicks"
            style={pianoStyle}
            onClick={handlePianoClicked}
          />
          {SoundComponent}
        </>
      )}
      <div className="gradient-wrapper" style={{ position: "relative", overflow: "hidden" }}>
        {gradients.map((imgProps, idx) => (
          <img {...imgProps} alt="gradient-image" key={idx} />
        ))}
        <div
          className="parallax-content"
          style={
            bgImgRef.current?.offsetHeight
              ? { height: bgImgRef.current?.offsetHeight }
              : { height: "100vh" }
          }>
          <div id="scene" className="scene" ref={sceneRef}>
            <div
              className="layer"
              id="specks"
              data-depth="0.1"
              style={{
                position: "absolute",
                zIndex: 999,
                minWidth: "1080px",
                width: "100vw",
                height: "auto",
              }}>
              {specks.map((style, idx) => (
                <img key={idx} src={speck} style={{ ...defaultSpeckStyle, ...style }} alt="speck" />
              ))}
            </div>
            <div className="layer" id="layer-1" data-depth="0.02">
              <img src={welcomeBG01} alt="layer1" style={getBGStyle(tick)} ref={bgImgRef} />
            </div>
            <div className="layer" id="layer-2" data-depth="0.04">
              <img src={welcomeBG02} alt="layer2" style={getBGStyle(tick)} />
            </div>
            <div className="layer" id="layer-3" data-depth="0.06">
              <img src={welcomeBG03} alt="layer3" style={getBGStyle(tick)} />
            </div>
            <div className="layer" id="layer-4" data-depth="0.08">
              <img src={welcomeBG04} alt="layer4" style={getBGStyle(tick)} />
            </div>
            <div
              className="layer"
              id="layer-5"
              data-depth="0.15"
              style={
                bgImgRef.current?.offsetHeight
                  ? { ...defaultFloatImgWrapperStyle, height: bgImgRef.current?.offsetHeight }
                  : defaultFloatImgWrapperStyle
              }>
              <div
                alt="layer-background"
                style={{ ...defaultFloatImgStyle(1), backgroundImage: `url(${dust1})` }}
              />
            </div>
            <div
              className="layer"
              id="layer-6"
              data-depth="0.25"
              style={
                bgImgRef.current?.offsetHeight
                  ? { ...defaultFloatImgWrapperStyle, height: bgImgRef.current?.offsetHeight }
                  : defaultFloatImgWrapperStyle
              }>
              <img
                src={dust2}
                alt="dust-background"
                style={{ ...defaultFloatImgStyle(2), backgroundImage: `url(${dust2})` }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelcomePage;
